import { Apollo } from 'apollo-angular';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalHostDirective } from '@app/core/directives/modal-host.directive';
import { ModalService } from '@app/core/services/modal/modal.service';
import { environment } from '@environments/environment';
import * as Sentry from '@sentry/browser';
import {
  User,
  Company,
  OnAcceptLineItemsGQL,
  NewOrderGQL,
  OnWontPackLineItemsGQL,
  OnRevertLineItemsToCreatedGQL,
  OnTemplateOutputStateChangeGQL,
  OnAddendumHeaderStateChangeGQL,
  OnReportCompleteGQL,
  OnRefreshReportGQL,
  OnPalletOutOutputStateChangeGQL,
  OnPalletInOutputStateChangeGQL,
  OnShipmentStateChangeGQL,
  OnAccountStatementStateChangeGQL,
  UserQuery,
  AccountStatement,
  OnArrivalQcGenerationStateChangeGQL,
  OnAccountingExportStateChangeGQL,
  CompanyStateEnum,
} from '@app/generated/graphql';
import { UserHelper } from '@app/core/helpers/user.helper';
import { UrlTrackerService } from '@app/core/services/url-tracker/url-tracker.service';
import { Router } from '@angular/router';

import { Angulartics2Mixpanel } from 'angulartics2';

import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { StatementDownloadService } from './payments/statement-download.service';
import { ArrivalQcGenerationService } from './quality-and-compliance/arrival-qc-generation.service';
import { UserService } from '@app/core/services/user.service';
import { TranslatorService } from '@app/core/services/translation/translator.service';
import { AccountingExportService } from '@app/shared/invoice-modules/accounting-export.service';
import { NavService } from '@app/core/services/nav.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PermissionService } from '@app/core/services/permission.service';

@Component({
  selector: 'ag-account',
  templateUrl: './account.component.html',
  providers: [TranslatorService, ConfirmationService, MessageService],
  standalone: false,
})
export class AccountComponent implements OnInit, OnDestroy {
  // subscription cleanup
  private destroy$ = new Subject<void>();

  // constants
  private readonly SEASON_SETUP_REDIRECT_KEY = 'season-setup-redirect' as const;

  @ViewChild(ModalHostDirective, { static: true })
  modalHost: ModalHostDirective;

  myCompany: Company;
  user: User;
  initialized = false;

  isExistingUser: boolean = null;
  isAgrigateUser: boolean = null;
  hasRedirectedToSeasonSetup: boolean = false;

  constructor(
    private modalService: ModalService,
    private urlTrackerService: UrlTrackerService,
    private router: Router,
    private newOrderGql: NewOrderGQL,
    private apollo: Apollo,
    private onAcceptLineItemsGql: OnAcceptLineItemsGQL,
    private onWontPackLineItemsGql: OnWontPackLineItemsGQL,
    private onRevertLineItemsToCreatedGql: OnRevertLineItemsToCreatedGQL,
    private angulartics2Mixpanel: Angulartics2Mixpanel,
    private onTemplateChangeGql: OnTemplateOutputStateChangeGQL,
    private onAddendumHeaderStateChangeGql: OnAddendumHeaderStateChangeGQL,
    private onPalletOutOutputStateChangeGql: OnPalletOutOutputStateChangeGQL,
    private onPalletInOutputStateChangeGql: OnPalletInOutputStateChangeGQL,
    private onReportCompleteGQL: OnReportCompleteGQL,
    private onRefreshReportGQL: OnRefreshReportGQL,
    private onShipmentStateChangeGQL: OnShipmentStateChangeGQL,
    private eventBus: EventBusService,
    private userService: UserService,
    private translatorService: TranslatorService,
    private onAccountStatementStateChangeGQL: OnAccountStatementStateChangeGQL,
    private statementDownloadService: StatementDownloadService,
    private onArrivalQcGenerationStateChangeGQL: OnArrivalQcGenerationStateChangeGQL,
    private arrivalQcGenerationService: ArrivalQcGenerationService,
    private onAccountingExportStateChangeGQL: OnAccountingExportStateChangeGQL,
    private accountingExportService: AccountingExportService,
    private navService: NavService,
    private permissionService: PermissionService,
  ) {
    // Ensures that permissions are initialized before any components within account module are initialized.
    // This guarantees that we have the user credentials and don't need to await for them in each component.
    this.permissionService.init();
    this._getSeasonSetupRedirectFromLocalStorage();
  }

  async ngOnInit(): Promise<void> {
    this.modalService.setHost(this.modalHost);

    this.userService
      .fetchUserQueryData$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((userQuery: UserQuery) => {
        if (!userQuery) {
          this.handleUserQueryError();
          return;
        }

        if (!this.initialized) {
          this.initialized = true;

          this.mixpanelTrackingSetup();

          // when dealing with share tokens the user ID will be null because
          // there is no user associated with a share token, only a company.
          let userId = 'unknown';
          if (this.user.id) {
            userId = this.user.id.toString();
          }

          Sentry.configureScope((scope) => {
            scope.setUser({
              id: userId,
              email: this.user.email,
            });
          });

          this.urlTrackerService.trackPreviousUrl();
        }

        this.translatorService.init();
      });

    this.userService.company$
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        tap((company: Company) => {
          this.myCompany = company;
        }),
        tap((company: Company) => this._handleSeasonSetupRedirect(company)),
      )
      .subscribe();

    this.userService.user$
      .pipe(takeUntil(this.destroy$), filter(Boolean))
      .subscribe((user: User) => {
        this.user = user;
        this.isExistingUser = UserHelper.isExistingUser(this.user);
        this.isAgrigateUser = UserHelper.isAgrigateUser(this.user);
      });

    this.newOrderGql.subscribe().subscribe((_) => {
      this.apollo.getClient().clearStore();
    });

    this.onAcceptLineItemsGql.subscribe().subscribe((res) => {
      this.apollo.getClient().clearStore();
    });

    this.onWontPackLineItemsGql.subscribe().subscribe((res) => {
      this.apollo.getClient().clearStore();
    });

    this.onRevertLineItemsToCreatedGql.subscribe().subscribe((res) => {
      this.apollo.getClient().clearStore();
    });

    this.initTemplateSubscription();
    this.initPPECBAddendumStateSubscription();
    this.initReportSubscription();
    this.initRefreshReportSubscription();
    this.initPalletOutSubscription();
    this.initPalletInSubscription();
    this.initShipmentStateSubscription();
    this.initAccountStatementSubscription();
    this.initArrivalQcGenerationSubscription();
    this.initAccountingExportSubscription();
  }

  private handleUserQueryError(): void {
    this.router.navigate(['account', '500'], { replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  mixpanelTrackingSetup() {
    if (!environment.angulartics2Enabled) return;

    // don't track events for agrigate users in production
    if (environment.production && this.isAgrigateUser) return;

    // set this to 'false' to enable frontend mixpanel tracking in development
    const skipDevTracking = true;
    if (environment.development && skipDevTracking) return;

    this.angulartics2Mixpanel.setUsername(this.user.externalIdentifier);
    this.angulartics2Mixpanel.setUserProperties({
      $first_name: this.user.firstName,
      $last_name: this.user.lastName,
      $email: this.user.email,
      $company_id: this.user.company.id,
      $role_name: this.user.role,
      $company_name: this.user.company.name,
    });
    this.angulartics2Mixpanel.startTracking();
  }

  initTemplateSubscription() {
    // templates are generated async on the backend, so we use a subscription to
    // dynamically update the link to a template document. A simple event bus
    // is used to tell interested components that they should fetch the data again.

    this.onTemplateChangeGql.subscribe().subscribe((res) => {
      const file = res?.data?.onTemplateOutputStateChange;

      if (!file) {
        return;
      }

      const type = file?.templateDataSourceType;
      const id = file?.templateDataSourceId;
      switch (type) {
        case 'AccountStatement':
        case 'Invoice':
        case 'LoadOutInstruction':
        case 'AddendumHeader':
          this.createNamespacedChannel(id, type);
      }
    });
  }

  initPPECBAddendumStateSubscription() {
    // addendums are generated async on the backend, so we use a subscription to
    // dynamically update the state of an addendum. A simple event bus
    // is used to tell interested components that they should fetch the data again.
    this.onAddendumHeaderStateChangeGql.subscribe().subscribe((res) => {
      const addendumHeader = res?.data?.onAddendumHeaderStateChange;
      const id = addendumHeader?.id;

      this.createNamespacedChannel(id, 'AddendumHeader');
    });
  }

  /**
   * An export notification is being done for the entire shipment,
   * multiple api calls have to be made many different records. We fetch
   * the shipment after it has completed with it's updated state and data.
   */
  initShipmentStateSubscription(): void {
    this.onShipmentStateChangeGQL.subscribe().subscribe((res) => {
      const shipment = res?.data?.onShipmentStateChange;
      const id = shipment?.id;

      this.createNamespacedChannel(id, 'Shipment');
    });
  }

  initPalletOutSubscription() {
    // pallet outs are generated async on the backend, so we use a subscription to
    // dynamically update the state of a pallet out. A simple event bus
    // is used to tell interested components that they should refetch the data.
    // in this case we want to refetch the addendum header, since the pallet out
    // is not a top level entity.
    this.onPalletOutOutputStateChangeGql.subscribe().subscribe((res) => {
      const palletOut = res?.data?.onPalletOutOutputStateChange;
      const parentId = palletOut?.addendumHeaderId;

      this.createNamespacedChannel(parentId, 'AddendumHeader');
    });
  }

  initPalletInSubscription() {
    // pallet ins are generated async on the backend, so we use a subscription to
    // dynamically update the state of a pallet in. A simple event bus
    // is used to tell interested components that they should refetch the data.
    // in this case we want to refetch the loi, since the pallet in
    // is not a top level entity.
    this.onPalletInOutputStateChangeGql.subscribe().subscribe((res) => {
      const palletIn = res?.data?.onPalletInOutputStateChange;
      const parentId = palletIn?.loadOutInstructionId;

      this.createNamespacedChannel(parentId, 'LoadOutInstruction');
    });
  }

  initReportSubscription() {
    this.onReportCompleteGQL.subscribe().subscribe((res) => {
      const requestUuid = res?.data?.onReportComplete?.requestUuid;
      const status = res?.data?.onReportComplete?.status;

      this.createNamespacedStateChannel(requestUuid, 'Report', status);
    });
  }

  initRefreshReportSubscription() {
    this.onRefreshReportGQL.subscribe().subscribe((res) => {
      const id = res?.data?.onRefreshReport.id;
      this.createNamespacedChannel(id.toString(), 'ActualReport');
    });
  }

  initAccountStatementSubscription() {
    // start watching for account statement changes

    this.statementDownloadService.setupStatementDownload();

    this.onAccountStatementStateChangeGQL.subscribe().subscribe((res) => {
      const accountStatement: AccountStatement =
        res?.data?.onAccountStatementStateChange;
      const id: string = accountStatement?.id;

      this.createGlobalChannel(id, 'AccountStatement');
    });
  }

  initArrivalQcGenerationSubscription(): void {
    this.onArrivalQcGenerationStateChangeGQL.subscribe().subscribe((res) => {
      this.arrivalQcGenerationService.updateArrivalQcGenerationData(
        res?.data?.onArrivalQcGenerationStateChange,
      );
    });
  }

  initAccountingExportSubscription(): void {
    this.onAccountingExportStateChangeGQL.subscribe().subscribe((res) => {
      const accountingExport = res.data?.onAccountingExportStateChange;
      const id: string = accountingExport?.id;

      this.createGlobalChannel(id, 'AccountingExport');
    });
  }

  // state is stored per page/ feature/ url etc. with a status variable
  createNamespacedStateChannel(id: string, type: string, status: string) {
    this.eventBus.setChannel(`Refetch${type}`, { id, status });
  }

  // state is stored per page/ feature/ url etc.
  createNamespacedChannel(id: string, type: string) {
    this.eventBus.setChannel(`Refetch${type}`, id);
  }

  // state is stored globally across all pages/ features/ urls etc.
  createGlobalChannel(id: string, type: string) {
    this.eventBus.setChannel(`Refetch${type}`, id, false);
  }

  /**
   * Redirects to the season setup page if the company is in the onboarding state.
   * The redirect is stored in local storage to prevent multiple redirects.
   */
  private _redirectToSeasonSetup(): void {
    if (this.hasRedirectedToSeasonSetup) {
      return;
    }

    this._setSeasonSetupRedirectToLocalStorage();
    this.router.navigate(['account', 'season-setup']);
  }

  /**
   * Gets the season setup redirect from local storage. This is used to prevent
   * redirecting to the season setup if the user has already been redirected once
   * before while the company is in the onboarding state.
   */
  private _getSeasonSetupRedirectFromLocalStorage(): void {
    const redirect = localStorage.getItem(this.SEASON_SETUP_REDIRECT_KEY);

    if (redirect) {
      this.hasRedirectedToSeasonSetup = true;
    } else {
      this.hasRedirectedToSeasonSetup = false;
    }
  }

  /**
   * Stores the season setup redirect in local storage. This is used to prevent
   * redirecting to the season setup if the user has already been redirected once
   * before while the company is in the onboarding state.
   */
  private _setSeasonSetupRedirectToLocalStorage(): void {
    localStorage.setItem(this.SEASON_SETUP_REDIRECT_KEY, 'true');
    this.hasRedirectedToSeasonSetup = true;
  }

  /**
   * Handles the season setup redirect. This is used to show the season setup
   * navigation and redirect to the season setup page if the company is in the
   * onboarding state.
   */
  private _handleSeasonSetupRedirect(company: Company): void {
    const seasonSetupStates: CompanyStateEnum[] = [
      CompanyStateEnum.Onboarding,
      CompanyStateEnum.ReOnboarding,
    ];
    if (seasonSetupStates.includes(company.state)) {
      this.navService.showSeasonSetupNavigation();
      this._redirectToSeasonSetup();
    } else {
      this.navService.hideSeasonSetupNavigation();
      this.hasRedirectedToSeasonSetup = false;
      // remove the redirect from local storage
      // this is to accomodate future cases where the
      // company is not in the onboarding state
      localStorage.removeItem(this.SEASON_SETUP_REDIRECT_KEY);
    }
  }
}
